import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@repo/utils";

/**
 * Arbitrary-variant className applied to `SheetContent` so its built-in
 * close button (rendered absolutely at top-right by `@repo/ui`'s Sheet)
 * is recolored white to sit legibly on top of the gradient header.
 *
 * Spread this onto the SheetContent className:
 * ```tsx
 * <SheetContent className={cn("...", sheetGradientCloseButtonClass)}>
 * ```
 */
export const sheetGradientCloseButtonClass =
  "[&>button]:text-white [&>button]:opacity-80 [&>button]:hover:opacity-100 [&>button]:focus:ring-white/60";

export interface SheetGradientHeaderProps {
  /** Lucide icon rendered in the circular bubble on the left. */
  icon: LucideIcon;
  /** Bold title text in white. */
  title: string;
  /** Optional subtitle rendered below the title in primary-soft. */
  description?: string;
  /** Extra class names applied to the outer header container. */
  className?: string;
}

/**
 * Gradient sheet header — used as the top band of any "action" Sheet
 * (create/edit flows). Signals to the user that they're about to take
 * an action that will create or modify data.
 *
 * Non-action sheets (read/detail views) should use the plain `SheetHeader`
 * from `./sheet` instead — the gradient is deliberately scoped so it
 * retains meaning.
 */
export function SheetGradientHeader({
  icon: Icon,
  title,
  description,
  className,
}: SheetGradientHeaderProps) {
  return (
    <div
      className={cn(
        "shrink-0 bg-gradient-to-r from-primary to-primary-hover px-6 py-5",
        className
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm">
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          {description && (
            <p className="text-sm text-primary-soft/90">{description}</p>
          )}
        </div>
      </div>
    </div>
  );
}
